import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  compoundMap,
  CompoundOccupancyError,
} from '@/api/CommandCentre/CompoundMap';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  CompoundMetadata,
  CompoundOccupancy,
  CompoundOccupancyStatus,
  CompoundSlots,
} from '@/types/CompoundMap';

export function useRecordedOccupancy(
  metadata: CompoundMetadata,
  geometry: CompoundSlots,
  scope: string,
  enabled: boolean,
) {
  const client = useQueryClient();
  const [visible, setVisible] = useState(() => !document.hidden);
  const [now, setNow] = useState(Date.now);
  const [blockedError, setBlockedError] =
    useState<CompoundOccupancyError | null>(null);
  const queryKey = useMemo(
    () => [
      QUERY_KEYS.compoundOccupancy,
      scope,
      metadata.siteId,
      metadata.datasetId,
      metadata.version,
    ],
    [scope, metadata.siteId, metadata.datasetId, metadata.version],
  );
  const query = useQuery<CompoundOccupancy | null>({
    queryKey,
    queryFn: ({ signal }) => compoundMap.occupancy(metadata, signal),
    enabled: enabled && !blockedError,
    retry: false,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: enabled && visible && !blockedError ? 30_000 : false,
  });
  const { refetch } = query;
  const unsafeError =
    query.error instanceof CompoundOccupancyError &&
    [401, 403, 404, 409].includes(query.error.status)
      ? query.error
      : null;
  const accessLost = Boolean(blockedError || unsafeError);
  const snapshot = enabled && !accessLost ? query.data : undefined;
  useLayoutEffect(() => {
    if (!unsafeError) return;
    setBlockedError(unsafeError);
    client.setQueryData<CompoundOccupancy | null>(queryKey, null);
  }, [client, queryKey, unsafeError]);
  useEffect(() => {
    if (!enabled) setBlockedError(null);
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    setVisible(!document.hidden);
    const changed = () => {
      setVisible(!document.hidden);
      setNow(Date.now());
      if (!document.hidden && !blockedError) void refetch();
    };
    document.addEventListener('visibilitychange', changed);
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      document.removeEventListener('visibilitychange', changed);
      window.clearInterval(timer);
    };
  }, [enabled, refetch, blockedError]);
  useEffect(() => {
    if (!enabled) return;
    return () => {
      void client.cancelQueries({ queryKey, exact: true });
      client.removeQueries({ queryKey, exact: true });
    };
  }, [client, queryKey, enabled]);
  const rows = useMemo(
    () => new Map(snapshot?.slots.map((row) => [row.slotId, row])),
    [snapshot?.slots],
  );
  const counts = useMemo(() => {
    const result: Record<CompoundOccupancyStatus, number> = {
      occupied: 0,
      unrecorded: 0,
      unknown: 0,
      conflict: 0,
    };
    geometry.features.forEach((feature) => {
      result[rows.get(feature.properties.slotId)?.status ?? 'unknown'] += 1;
    });
    return result;
  }, [geometry, rows]);
  return {
    snapshot,
    rows,
    counts,
    accessLost,
    error: enabled ? (blockedError ?? query.error) : null,
    fetching: query.isFetching,
    stale: Boolean(
      snapshot &&
        (query.error ||
          Math.max(now, Date.now()) - Date.parse(snapshot.asOf) > 60_000),
    ),
    refresh: () => {
      if (blockedError) setBlockedError(null);
      else if (!query.isFetching) void query.refetch();
    },
  };
}
